import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Grid, IconButton, Snackbar, Tooltip, Typography } from "@mui/material";
import React, { ReactNode, useEffect, useState } from "react";
import { useActividadesContext } from "../Hook/useActividades";
import { ActionsButtons } from "./components/ActionsButtons";
import { ActionsButtonsFooter } from "./components/ActionsButtonsFooter";
import { BodyUmmiaAppsResult } from "./components/BodyUmmiaAppsResult";
import HeaderApps from "./HeaderApps";
import NotEnoughCredits from "../../UmmIApps/NotEnoughCredits";
import UmmIAChatModal from "../../UmmIAChatAI/UmmIAChatModal";
import { ChatUIProvider, useChatUI } from "../../UmmIAChatAI/ChatUIContext";
import UmmIAAgentIcon from "../../UmmIAChatAI/svg/UmmIAAgentIcon.svg";

type LayoutProps = {
  children: ReactNode;
  title?: string;
  imageSrc: string;
  description: string;
  titleColor: string;
  backgroundColorImage: string;
  isButtonDisabled: boolean;
  onBack?: () => void;
  colorIconBack: string;
};

const CHAT_PANEL_WIDTH = 380;

const LayoutBody: React.FC<LayoutProps> = ({
  onBack,
  imageSrc,
  title,
  description,
  backgroundColorImage,
  children,
  titleColor,
  isButtonDisabled,
  colorIconBack,
}) => {
  const { toggleVista, respuestaGenerada, mostrarCampos, editMode } = useActividadesContext();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { isChatOpen, openChat, closeChat } = useChatUI();

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (editMode) openChat();
    else closeChat();
  }, [editMode]);

  return (
    <Box
      sx={{
        display: "block",
        width: "100%",
        minHeight: "100dvh",
        bgcolor: "background.default",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      {/* Columna izquierda: chat */}
      <Box
        component="aside"
        sx={{
          position: "fixed",
          left: 70,
          top: 70,
          height: "93dvh",
          width: isChatOpen ? CHAT_PANEL_WIDTH : 0,
          bgcolor: isChatOpen ? "background.paper" : "transparent",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          overflow: "hidden",
          zIndex: 10,
          borderRight: isChatOpen ? "1px solid #e0e0e0" : "none",
        }}
      >
        <Box
          sx={{
            p: isChatOpen ? 1.25 : 0,
            height: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            transition: "padding 200ms ease",
          }}
        >

          <UmmIAChatModal
            open={isChatOpen}
            onClose={closeChat}
            onCollapse={closeChat}
            mode="inline"
            styleOverrides={{
              width: "100%",
              height: "100%",
              borderRadius: "20px",
            }}
          />

        </Box>
      </Box>
      {/* Handle flotante para abrir el chat cuando está cerrado */}
      {editMode && !isChatOpen && (
        <Tooltip title="Abrir UmmIA Agent" arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#1D3D4D",
                color: "#FFF",
                fontFamily: "Montserrat",
                fontSize: "0.875rem",
                padding: "8px 12px",
                borderRadius: "8px",
              },
            },
            arrow: { sx: { color: "#1D3D4D" } },
          }}>
          <IconButton
            onClick={openChat}
            aria-label="Abrir UmmIA Agent"
            sx={{
              position: "fixed",
              left: 90,
              top: "50%", 
              bgcolor: "#4C7592",
              transform: "translateY(-50%)",
              zIndex: 11,
              width: 36,
              height: 36,
              color: "#FFF",
              borderRadius: "12px",
              boxShadow: 3,
              "&:hover": { bgcolor: "#4C7592" },
            }}
          >
            <Box
              component="img"
              src={UmmIAAgentIcon}
              alt="Abrir chat"
              sx={{ width: 35, height: 35, display: "block" }}
            />
          </IconButton>
        </Tooltip>
      )}


      {/* Columna derecha: contenido principal */}
      <Box
        component="main"
        sx={{
          ml: isChatOpen ? `${CHAT_PANEL_WIDTH}px` : 5,
          transition: "margin-left 200ms ease",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {/* Header */}
        <Box sx={{ flexShrink: 0 }}>
          <HeaderApps
            imageSrc={imageSrc}
            title={title}
            titleColor={titleColor}
            description={description}
            backgroundColorImage={backgroundColorImage}
            onBack={onBack}
            colorIconBack={colorIconBack}
          />
        </Box>

        <Box sx={{ p: 3, position: "relative" }}>
          <Grid container>
            {respuestaGenerada && mostrarCampos && (
              <Box
                sx={{
                  m: "auto",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "90%",
                  height: "80px",
                  transform: "scale(0.8)",
                  transformOrigin: "top center",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={toggleVista}
                  sx={{
                    minWidth: "200px",
                    borderRadius: 5,
                    border: "1px solid #336B87",
                    color: "#336B87",
                    fontWeight: "Bold",
                    m: "auto",
                    mt: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{ fontFamily: "montserrat", fontWeight: "Bold", fontSize: "13px" }}
                    >
                      {"mostrar respuesta"}
                    </Typography>
                    <VisibilityIcon />
                  </Box>
                </Button>
              </Box>
            )}

            {mostrarCampos ? (
              <>{children}</>
            ) : (
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "auto",
                }}
              >
                <ActionsButtons />
                <BodyUmmiaAppsResult />
              </Grid>
            )}

            <ActionsButtonsFooter isButtonDisabled={isButtonDisabled} />

            <Snackbar
              open={snackbarOpen}
              autoHideDuration={3000}
              onClose={handleSnackbarClose}
              message="Datos fueron persistidos correctamente"
            />
          </Grid>

          <NotEnoughCredits />
        </Box>
      </Box>
    </Box>
  );
};

const UmmiaAppsLayout: React.FC<LayoutProps> = (props) => (
  <ChatUIProvider>
    <LayoutBody {...props} />
  </ChatUIProvider>
);

export default UmmiaAppsLayout;
